import { ReactElement } from 'react';
import { LAMBDA_DEFAULT_PAGE_SIZE } from '@localstack/constants';
import {
  getAWSRowCount,
  AwsClientOverrides,
  getSchema,
  useAwsGetter,
  resolveWebFunctionLogGroup,
} from '@localstack/services';
import { Link, Stack, Typography } from '@mui/material';
import { LambdaWebRevisionSummary } from '@localstack/types';

import { MagicTable } from '../../magic/MagicTable';

export type LambdaWebRevisionsTableProps = {
  page?: number;
  onPageChange?: (page: number) => void;
  hasMore?: boolean;
  loading?: boolean;
  revisions: LambdaWebRevisionSummary[];
  onSelect?: (ids: string[]) => void;
  functionName?: string;
  clientOverrides?: AwsClientOverrides;
  getRevisionLogsUrl?: (logGroupName: string, logStreamName: string) => string;
};

type RevisionLogLinkProps = {
  functionName?: string;
  revisionId?: string;
  clientOverrides?: AwsClientOverrides;
  getRevisionLogsUrl?: (logGroupName: string, logStreamName: string) => string;
};

const RevisionLogLink = ({
  functionName,
  revisionId,
  clientOverrides,
  getRevisionLogsUrl,
}: RevisionLogLinkProps): ReactElement => {
  const { data: revision } = useAwsGetter(
    'LambdaWeb',
    'getWebFunctionRevision',
    [{ functionName: functionName ?? '', revisionId: revisionId ?? '' }],
    { clientOverrides, enable: !!functionName && !!revisionId },
  );
  const configuredLogGroupName = revision?.serviceConfig?.telemetryConfig?.loggingConfig?.logGroup;
  const { data: logGroups } = useAwsGetter(
    'CloudWatchLogs',
    'describeLogGroups',
    [{ logGroupNamePrefix: configuredLogGroupName ?? '' }],
    { clientOverrides, enable: !!configuredLogGroupName, swrOverrides: { refreshInterval: 5000 } },
  );
  const logGroupName = resolveWebFunctionLogGroup(configuredLogGroupName, logGroups?.logGroups);
  const { data: logStreams } = useAwsGetter(
    'CloudWatchLogs',
    'describeLogStreams',
    [{ logGroupName: logGroupName ?? '' }],
    { clientOverrides, enable: !!logGroupName, swrOverrides: { refreshInterval: 5000 } },
  );
  const logStreamName = logStreams?.logStreams?.find((stream) =>
    stream.logStreamName?.includes(`[${revisionId}]`),
  )?.logStreamName;

  return logGroupName && logStreamName && getRevisionLogsUrl ? (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography component="span" variant="inherit">
        {revisionId}
      </Typography>
      <Link
        href={getRevisionLogsUrl(logGroupName, logStreamName)}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        aria-label={`View logs for revision ${revisionId}`}
      >
        View logs
      </Link>
    </Stack>
  ) : (
    <>{revisionId}</>
  );
};

export const LambdaWebRevisionsTable = ({
  page = 0,
  onPageChange,
  hasMore = false,
  loading,
  revisions,
  onSelect,
  functionName,
  clientOverrides,
  getRevisionLogsUrl,
}: LambdaWebRevisionsTableProps): ReactElement => (
  <MagicTable
    schema={getSchema('LambdaWeb')}
    entry="FunctionRevisionSummary"
    idAttribute="revisionId"
    order={['revisionId', 'state', 'description', 'createdAt']}
    customWidths={{ revisionId: 260 }}
    rows={revisions}
    page={page}
    onPageChange={onPageChange}
    paginationMode="server"
    pageSize={LAMBDA_DEFAULT_PAGE_SIZE}
    rowsPerPageOptions={[LAMBDA_DEFAULT_PAGE_SIZE]}
    rowCount={getAWSRowCount({ page, DEFAULT_PAGE_SIZE: LAMBDA_DEFAULT_PAGE_SIZE, hasMore, array: revisions })}
    selectable={!!onSelect}
    onSelect={onSelect}
    loading={loading}
    externalFields={{
      revisionId: (row: LambdaWebRevisionSummary) => (
        <RevisionLogLink
          functionName={functionName}
          revisionId={row.revisionId}
          clientOverrides={clientOverrides}
          getRevisionLogsUrl={getRevisionLogsUrl}
        />
      ),
    }}
  />
);
